// @ts-nocheck

import React, { useCallback } from 'react';
import styled from 'styled-components';

import { EndedSvg } from '../../assets/svg/history';
import Formatter from '../../i18n/formatter';
import { __ } from '../../i18n/l10n';
import { MatchEntityStatus } from '../../states/entities/matches';
import colors from '../../styled-components/palette';
import { type MatchStatus } from '../../types/match';
import TimerContainer from '../DefaultTimer/DefaultTimerContainer';
import FlagIcon from '../FlagIcon';
import Link from '../Link';

const MultimatchItemTheme = {
  prematchTimerColor: colors.neutralgrey400,
  liveColor: colors.yellowwinamax450,
  endedColor: colors.bluegrey200,
};

const Wrapper = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 85%;
  min-height: 2.7em;
  padding: 0 0.5em;

  user-select: none;
  -webkit-tap-highlight-color: transparent;
`;

const StyledCategoryFlagIcon = styled(FlagIcon)`
  display: inline-block;
  width: 1.3em;
  height: 1.3em;
  border-radius: 0.25em;
`;

const StyledFlagIcon = styled(FlagIcon)`
  display: inline-block;
  width: 1.3em;
  min-width: 1.3em;
  height: 1.3em;
  border-radius: 0.4em;
  margin: 0 0.2em;

  ${({ $right }) => ($right ? `margin-left: 0;` : 'margin-right: 0;')}
`;

const CompetitorsBlock = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;

  padding: 0 0.4em;
  color: white;
`;

const Competitor = styled.div`
  display: flex;
  flex: 1 0 0;
  align-items: center;
  ${({ $right }) => ($right ? `justify-content: left;` : 'justify-content: right; text-align: right;')}
  overflow: hidden;
  line-height: 1.1em;
`;

const ScoreBlock = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.2em;
`;

const SideBlock = styled.div`
  display: flex;
  width: 4.5em;
  align-items: center;

  ${({ $right }) => ($right ? `justify-content: right;` : '')}
`;

const TimerBlock = styled(SideBlock)`
  text-align: right;
`;

const Dash = styled.div`
  display: inline-block;
  padding: 0 0.3em;
`;

const Score = styled.div`
  display: flex;
  align-items: center;

  margin: 0 0.3em 0.1em 0.3em;
  ${({ $right }) => ($right ? `margin-left: 0;` : 'margin-right: 0;')}

  color: ${({ $yellow }) => ($yellow ? MultimatchItemTheme.liveColor : MultimatchItemTheme.endedColor)};
`;

const StyledTimer = styled(TimerContainer)`
  padding-right: 0.2em;
  font-size: 0.9em;

  color: ${MultimatchItemTheme.liveColor};
`;

const StyledCalendarDate = styled.span`
  color: ${MultimatchItemTheme.prematchTimerColor};
  font-size: 0.86em;

  &:first-letter {
    text-transform: uppercase;
  }
`;

const StyledEndedIcon = styled(EndedSvg)`
  width: 1em;
  height: 100%;
`;

type PropsType = {
  className?: string;
  matchId: number;
  flagId?: number;
  matchStart: number;
  competitor1Name: string;
  competitor1Flag?: string;
  competitor2Name: string;
  competitor2Flag?: string;
  homeScore?: number;
  awayScore?: number;
  status?: MatchStatus;
  isOvertime: boolean;
};

const MultimatchItem = ({
  matchId,
  flagId,
  matchStart,
  competitor1Name,
  competitor1Flag,
  competitor2Name,
  competitor2Flag,
  homeScore,
  awayScore,
  className,
  status,
  isOvertime,
}: PropsType) => {
  const renderScore = useCallback(
    (score, right) => {
      if (MatchEntityStatus.isPrematch(status) || score === null || score === undefined) {
        return null;
      }

      let yellow = false;
      if (MatchEntityStatus.isLive(status) && !isOvertime) {
        yellow = true;
      }
      return (
        <Score $yellow={yellow} $right={right}>
          {score}
        </Score>
      );
    },
    [status, isOvertime],
  );
  const renderTimer = useCallback(() => {
    if (MatchEntityStatus.isPrematch(status)) {
      return <StyledCalendarDate>{Formatter.formatMatchStart(matchStart, __)}</StyledCalendarDate>;
    }

    if (MatchEntityStatus.isEnded(status) || isOvertime) {
      return <StyledEndedIcon />;
    }

    if (MatchEntityStatus.isLive(status)) {
      return <StyledTimer matchId={matchId} showPeriod />;
    }

    return null;
  }, [matchId, matchStart, status, isOvertime]);

  return (
    <Wrapper className={className} to={`/match/${matchId}`}>
      <SideBlock>{flagId ? <StyledCategoryFlagIcon flagId={flagId} /> : null}</SideBlock>
      <CompetitorsBlock>
        <Competitor>
          {competitor1Name} {competitor1Flag ? <StyledFlagIcon flagId={competitor1Flag} /> : null}
        </Competitor>
        <ScoreBlock>
          {renderScore(homeScore)}
          <Dash>-</Dash>
          {renderScore(awayScore, true)}
        </ScoreBlock>
        <Competitor $right>
          {competitor2Flag ? <StyledFlagIcon flagId={competitor2Flag} $right /> : null} {competitor2Name}
        </Competitor>
      </CompetitorsBlock>
      <TimerBlock $right>{renderTimer()}</TimerBlock>
    </Wrapper>
  );
};

export default React.memo<PropsType>(MultimatchItem);
